/**
 * usePresence — hook for multi-user awareness
 *
 * Sends a heartbeat every 30 seconds and polls active users
 * for the given template. Returns list of other active users.
 *
 * Uses mountedRef guards to prevent state updates after unmount.
 * `removePresence` in presenceApi uses `keepalive: true` so the
 * server receives the leave request even during navigation.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  sendHeartbeat,
  getPresence,
  removePresence,
  type PresenceEntry,
} from "../../utils/presenceApi";

const HEARTBEAT_INTERVAL = 30_000; // 30 seconds
const POLL_INTERVAL = 15_000; // 15 seconds

export function usePresence(
  templateId: string | null,
  username: string | null,
  displayName: string | null,
  avatarUrl?: string,
) {
  const [others, setOthers] = useState<PresenceEntry[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  // Send heartbeat
  const heartbeat = useCallback(() => {
    if (!templateId || !username || !displayName || !mountedRef.current) return;
    sendHeartbeat(templateId, username, displayName, avatarUrl).catch((e) => {
      // Silently ignore errors when component is unmounted
      if (mountedRef.current) {
        console.log("[Presence] heartbeat error:", e);
      }
    });
  }, [templateId, username, displayName, avatarUrl]);

  // Poll active users
  const poll = useCallback(() => {
    if (!templateId || !username || !mountedRef.current) return;
    getPresence(templateId)
      .then((users) => {
        if (!mountedRef.current) return;
        // Filter out ourselves
        setOthers(users.filter((u) => u.username !== username));
      })
      .catch((e) => {
        if (mountedRef.current) {
          console.log("[Presence] poll error:", e);
        }
      });
  }, [templateId, username]);

  useEffect(() => {
    mountedRef.current = true;

    if (!templateId || !username || !displayName) {
      setOthers([]);
      return;
    }

    // Immediate heartbeat + poll
    heartbeat();
    poll();

    intervalRef.current = setInterval(heartbeat, HEARTBEAT_INTERVAL);
    pollRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
      // Announce departure — uses keepalive: true internally so it
      // survives the component unmount without causing "connection closed"
      removePresence(templateId, username);
    };
  }, [templateId, username, displayName, heartbeat, poll]);

  return others;
}
